import React from 'react';

import { Card, CardBody } from 'Components/Card';
import { PrimaryButton } from 'Components/Button';

import PollsApi from 'Services/Api/MainStage/Poll';
import { Alert } from 'Components/Partials';

import { translation } from 'Services/TranslationHelpers';

export default class Poll extends React.Component {
    /**
     * @var containerRef
     * @type {null|HTMLDivElement}
     */
    containerRef = null;

    /**
     * @var titleRef
     * @type {null|HTMLDivElement}
     */
    titleRef = null;

    /**
     * @var formRef
     * @type {null|HTMLDivElement}
     */
    footerRef = null;

    /**
     * @var returnTimeout
     * @type {null}
     */
    returnTimeout = null;

    /**
     * @var state
     */
    state = {
        vote: null,
        ranking: [],
        working: false,
        alert: null,
        submission: null,
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        clearTimeout(this.returnTimeout);
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        if (this.props.poll) {
            this.fetchSubmission();
        }

        if (window.base.main_stage.layout === 'tabs') {
            this.setSize();
        }
    };

    /**
     * @method componentDidUpdate
     * @param {object} prevProps
     */
    componentDidUpdate = (prevProps) => {
        if (window.base.main_stage.layout === 'tabs' && prevProps.height !== this.props.height) {
            this.setSize();
        }

        if (prevProps.poll?.id !== this.props.poll?.id && this.props.poll) {
            this.fetchSubmission();
        }
    };

    /**
     * @method setSize
     */
    setSize = () => {
        if (!this.containerRef) {
            return;
        }

        this.containerRef.style.height = `${this.props.height - (this.footerRef ? this.footerRef.clientHeight : 0) - this.titleRef.clientHeight - 48}px`;
    };

    /**
     * @method fetchSubmission
     * @return {Promise<void>}
     */
    fetchSubmission = async () => {
        // If there are no submissions then we don't waste an api call checking for this user.
        // if (this.props.poll.submission_count === 0) {
        //     return this.setState({
        //         submission: null
        //     });
        // }

        const request = await PollsApi.get({
            session: this.props.sessionId,
        }, {
            poll_id: this.props.poll.id,
        });

        const submission = request?.data?.data && request?.data?.data?.id
            ? request.data.data
            : null;

        // If this is a ranking poll and the submission contains a ranking array,
        // load that ranking into component state so the UI displays the user's submitted order.
        if (submission && this.props.poll?.type === 'ranking' && Array.isArray(submission.ranking)) {
            return this.setState({
                submission,
                ranking: submission.ranking,
            });
        }

        return this.setState({
            submission,
        });
    };

    /**
     * @method handleVote
     * @param {string} vote
     */
    handleVote = (vote) => {
        this.setState({
            vote,
        });
    };

    handleRankingChange = (ranking) => {
        this.setState({ ranking });
    };

    /**
     * @method handleSubmitVote
     * @return {Promise<void>}
     */
    handleSubmitVote = async () => {
        this.setState({ working: true });

        let payload = {
            poll_id: this.props.poll.id,
        };

        if (this.props.poll.type === 'ranking') {
            payload.ranking = this.state.ranking;
        } else {
            payload.answer_id = this.state.vote;
        }

        const request = await PollsApi.post({
            session: this.props.sessionId,
        }, payload);

        if (request.success) {
            return this.setState({
                alert: {
                    type: 'success',
                    message: translation('main_stage_frontend', 'thank_you_for_submission'),
                },
                working: false,
                submission: request.data.data,
            }, () => {
                if (!this.props.onSuccess)  {
                    return;
                }

                this.returnTimeout = setTimeout(() => this.props.onSuccess(), 2000);
            });
        }

        this.setState({
            alert: {
                type: 'error',
                message: translation('main_stage_frontend', 'submission_problem'),
                errors: request.errors,
            },
            working: false,
        });
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const { poll, bgColour = 'bg-app-gray' } = this.props;
        const { vote, ranking, working, alert, submission } = this.state;

        if (!poll) {
            return (
                <Card className={`h-full ${bgColour} text-app-blue rounded-b-xl`} bgColour="bg-app-blue" titleColor="text-white">
                    <CardBody className="h-full flex flex-col my-10">
                        <h1 className="text-2xl font-bold" ref={ref => this.titleRef = ref}>
                            {translation('main_stage_frontend', 'no_polls')}
                        </h1>
                    </CardBody>
                </Card>
            );
        }

        return (
            <Card className={`h-full ${bgColour} text-app-blue  rounded-b-xl`} bgColour="bg-app-blue" titleColor="text-white">
                <CardBody className="h-full flex flex-col">
                    <h1 className="text-lg font-bold mb-5 text-app-agenda-lighter-blue" ref={ref => this.titleRef = ref}>
                        {poll.question}
                    </h1>

                    <div ref={ref => this.containerRef = ref} className="overflow-y-auto">
                        {alert !== null && submission !== null && (
                            <div className="p-4">
                                <Alert { ...alert} />
                            </div>
                        )}

                        {poll.type === 'ranking' ? (
                            <RankingPoll
                                answers={poll.answers}
                                ranking={ranking}
                                onChange={this.handleRankingChange}
                                disabled={!!submission}
                            />
                        ) : (
                            poll.answers.map(a => (
                                <div className="relative p-2 flex items-center bg-app-agenda-lighter-blue my-5 rounded-full" key={"answer-" + a.id}>
                                    <div className="flex items-center">
                                        <input
                                            id={`vote-${a.id}`}
                                            type="radio"
                                            className={`focus:ring-indigo-500 h-4 w-4 ml-2 text-app-blue border-app-gray ${submission ? '' : 'cursor-pointer'}`}
                                            checked={a.id === vote || (submission !== null && submission.answer_id === a.id)}
                                            onChange={() => this.handleVote(a.id)}
                                            disabled={submission !== null}
                                        />
                                    </div>
                                    <label htmlFor={`vote-${a.id}`}
                                           className={`ml-3 flex flex-col text-white ${submission ? '' : 'cursor-pointer'}`}
                                    >
                                        <span className="block font-medium">
                                            {a.answer}
                                        </span>
                                    </label>
                                </div>
                            ))
                        )}
                    </div>

                    <div ref={ref => this.footerRef = ref} className="flex justify-end pb-4">
                        <PrimaryButton
                            onClick={this.handleSubmitVote}
                            disabled={
                                poll.type === 'ranking'
                                    ? ranking.length !== poll.answers.length || submission
                                    : !vote || submission
                            }
                            working={working}
                            text={translation('main_stage_frontend', 'vote')}
                            className={"rounded-xl"}
                        />
                    </div>
                </CardBody>
            </Card>
        );
    }
}

// RankingPoll component (simple drag-and-drop) - enhanced with touch support for mobile
function RankingPoll({ answers, ranking, onChange, disabled }) {
    const [localRanking, setLocalRanking] = React.useState(
        ranking.length === answers.length
            ? ranking
            : answers.map(a => a.id)
    );

    const draggingIndexRef = React.useRef(null);
    const draggingTouchIdRef = React.useRef(null);
    const itemRefs = React.useRef([]);
    const listRef = React.useRef(null);
    const [overIndex, setOverIndex] = React.useState(null);

    // small helper to compare simple arrays of primitives
    const arraysEqual = (a = [], b = []) => {
        if (a === b) return true;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    };

    // Keep localRanking in sync when answers or incoming ranking changes,
    // but only update if the desired order actually differs (avoid re-setting state each render).
    React.useEffect(() => {
        const desired = ranking.length === answers.length ? ranking : answers.map(a => a.id);
        if (!arraysEqual(desired, localRanking)) {
            setLocalRanking(desired);
        }
    }, [answers, ranking, localRanking]);

    // Move an item and notify parent only when change originates from user action.
    const move = (from, to) => {
        if (disabled) return;
        if (from === to) return;
        const updated = [...localRanking];
        const safeFrom = Math.max(0, Math.min(updated.length - 1, from));
        const safeTo = Math.max(0, Math.min(updated.length - 1, to));
        const [item] = updated.splice(safeFrom, 1);
        updated.splice(safeTo, 0, item);
        setLocalRanking(updated);
        if (typeof onChange === 'function') {
            onChange(updated);
        }
    };

    const handleDragStart = (e, idx) => {
        if (disabled) {
            e.preventDefault();
            return;
        }
        draggingIndexRef.current = idx;
        try {
            e.dataTransfer.setData('text/plain', String(idx));
        } catch (err) {
            // ignore
        }
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, idx) => {
        if (disabled) return;
        e.preventDefault(); // allow drop
        setOverIndex(idx);
        try {
            e.dataTransfer.dropEffect = 'move';
        } catch (err) {
            // ignore
        }
    };

    const handleDrop = (e, idx) => {
        if (disabled) return;
        e.preventDefault();
        let from = null;
        try {
            const d = e.dataTransfer.getData('text/plain');
            from = Number.isNaN(Number(d)) ? null : Number(d);
        } catch (err) {
            from = null;
        }
        if (from === null && draggingIndexRef.current !== null) {
            from = draggingIndexRef.current;
        }
        if (from !== null) {
            move(from, idx);
        }
        draggingIndexRef.current = null;
        setOverIndex(null);
    };

    const handleDragEnd = () => {
        draggingIndexRef.current = null;
        setOverIndex(null);
    };

    // Touch handlers for mobile
    const getTouchById = (touches, id) => {
        for (let i = 0; i < touches.length; i++) {
            if (touches[i].identifier === id) return touches[i];
        }
        return null;
    };

    const findIndexFromY = (clientY) => {
        const items = itemRefs.current || [];
        if (!items.length) return 0;
        for (let i = 0; i < items.length; i++) {
            const el = items[i];
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            const mid = rect.top + rect.height / 2;
            if (clientY < mid) {
                return i;
            }
        }
        return items.length - 1;
    };

    const handleTouchStart = (e, idx) => {
        if (disabled) return;
        const touch = e.changedTouches[0];
        draggingTouchIdRef.current = touch.identifier;
        draggingIndexRef.current = idx;
        setOverIndex(idx);
    };

    const handleTouchMove = (e) => {
        if (disabled) return;
        const touch = getTouchById(e.changedTouches, draggingTouchIdRef.current);
        if (!touch) return;
        // prevent page scroll while dragging this item
        e.preventDefault();
        const clientY = touch.clientY;
        const targetIndex = findIndexFromY(clientY);
        if (typeof targetIndex === 'number' && targetIndex !== draggingIndexRef.current) {
            move(draggingIndexRef.current, targetIndex);
            // update dragging index to the new position
            draggingIndexRef.current = targetIndex;
            setOverIndex(targetIndex);
        }
    };

    const handleTouchEnd = (e) => {
        if (disabled) return;
        const touch = getTouchById(e.changedTouches, draggingTouchIdRef.current);
        if (!touch) {
            // ended touch isn't ours
            return;
        }
        draggingTouchIdRef.current = null;
        draggingIndexRef.current = null;
        setOverIndex(null);
    };

    return (
        <div ref={listRef}>
            {localRanking.map((id, idx) => {
                const answer = answers.find(a => a.id === id);
                const isOver = overIndex === idx;
                return (
                    <div
                        key={"ranking-item-" + id}
                        ref={el => itemRefs.current[idx] = el}
                        draggable={!disabled}
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={(e) => handleDrop(e, idx)}
                        onDragEnd={handleDragEnd}
                        onTouchStart={(e) => handleTouchStart(e, idx)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                        style={{ touchAction: 'none' }}
                        className={`relative p-4 flex bg-app-agenda-lighter-blue my-5 rounded-full items-center ${disabled ? 'opacity-60' : 'cursor-move'} ${isOver ? 'opacity-90 ring-2 ring-white/30' : ''}`}
                        aria-grabbed={draggingIndexRef.current === idx}
                    >
                        <span className="mr-4 font-bold text-white">{idx + 1}</span>
                        <span className="flex-1 text-white">{answer?.answer}</span>
                        {!disabled && (
                            <span className="flex space-x-2 ml-4">
                                <button
                                    type="button"
                                    className="text-white px-2"
                                    disabled={idx === 0}
                                    onClick={() => move(idx, idx - 1)}
                                >↑</button>
                                <button
                                    type="button"
                                    className="text-white px-2"
                                    disabled={idx === localRanking.length - 1}
                                    onClick={() => move(idx, idx + 1)}
                                >↓</button>
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
